import CloseIcon from '@mui/icons-material/Close'
import { Button } from '@mui/material'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { Info } from 'lucide-react'
import React from 'react'
import { CovarianceParametersDialog } from './CovarianceParametersDialog'

interface AdvancedSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: (settings: {
    riskModel: string;
    covarianceMatrix: string;
    varConfidence: string;
    timePeriodNumber: string;
    timePeriodUnit: string;
  }) => void;
  initialSettings?: {
    riskModel: string;
    covarianceMatrix: string;
    varConfidence: string;
    timePeriodNumber: string;
    timePeriodUnit: string;
  };
}

export const riskModelOptions = [
  { value: 'parametric', label: 'Parametric (Variance-Covariance)' },
  { value: 'historical', label: 'Historical Simulation', disabled: true },
  { value: 'monte-carlo', label: 'Monte Carlo Simulation', disabled: true },
];

export function AdvancedSettingsDialog({ open, onOpenChange, onSave, initialSettings }: AdvancedSettingsDialogProps) {
  const theme = useTheme()
  const [riskModel, setRiskModel] = React.useState(initialSettings?.riskModel || 'parametric');
  const [covarianceMatrix, setCovarianceMatrix] = React.useState(initialSettings?.covarianceMatrix || 'GCV1415');
  const [varConfidence, setVarConfidence] = React.useState(initialSettings?.varConfidence || '95');
  const [timePeriodNumber, setTimePeriodNumber] = React.useState(initialSettings?.timePeriodNumber || '1');
  const [timePeriodUnit, setTimePeriodUnit] = React.useState(initialSettings?.timePeriodUnit || 'day');
  const [showCovarianceDialog, setShowCovarianceDialog] = React.useState(false);
  const [confidenceError, setConfidenceError] = React.useState('');

  // Update state when initialSettings change (when dialog opens)
  React.useEffect(() => {
    if (initialSettings) {
      if (initialSettings.riskModel) setRiskModel(initialSettings.riskModel);
      if (initialSettings.covarianceMatrix) setCovarianceMatrix(initialSettings.covarianceMatrix);
      if (initialSettings.varConfidence) setVarConfidence(initialSettings.varConfidence);
      if (initialSettings.timePeriodNumber) setTimePeriodNumber(initialSettings.timePeriodNumber);
      setTimePeriodUnit(initialSettings.timePeriodUnit || 'day');
    }
  }, [initialSettings]);

  const assetClassOptions = [
    { value: 'fixed-income', label: 'Fixed Income' },
    { value: 'equity', label: 'Equity' },
    { value: 'multi-asset', label: 'Multi-Asset' },
    { value: 'alternatives', label: 'Alternatives' },
  ];

  const timePeriodUnitOptions = [
    { value: 'day', label: 'Day(s)' },
    { value: 'month', label: 'Month(s)' },
    { value: 'year', label: 'Year(s)' },
  ];

  const covarianceMatrixOptions = [
    { value: 'GCV1415', label: 'GCV14 FI+EQ+Comm - Base' },
    { value: 'GCV1416', label: 'GCV14 FI+EQ+Comm - Exp.D = .94', disabled: true },
    // Hidden options (kept for future use)
    // { value: 'CREDIT', label: 'Global Credit Covariance - I' },
    // { value: 'GCAPA', label: 'Covariance Matrix for GCAPA' },
    // { value: 'GCV1201', label: 'GCV12 EMD - Base' },
    // { value: 'GCV1202', label: 'GCV12 EMD - Asynch' },
    // { value: 'GCV1203', label: 'GCV12 EMD Exp. Decay = .97' },
    // { value: 'GCV1204', label: 'GCV12 EMD Exp. Decay = .94' },
    // { value: 'GCV1205', label: 'GCV12 EMD Shaptse - Base' },
    // { value: 'GCV1206', label: 'GCV12 EMD Shaptse - Exp.D = .97' },
    // { value: 'GCV1207', label: 'GCV12 EMD Shaptse - Exp.D = .94' },
    // { value: 'GCV1401', label: 'GCV14 EMD - Base' },
    // { value: 'IQDL94', label: 'IQ Daily Exp. Decay = 0.94' },
    // { value: 'IQDL97', label: 'IQ Daily Exp. Decay = 0.97' },
    // { value: 'IQDLEQ', label: 'IQ Daily Equal' },
  ];

  const timePeriodOptions = [
    { value: '1-day', label: '1 Day' },
    { value: '5-day', label: '5 Days' },
    { value: '10-day', label: '10 Days' },
    { value: '1-month', label: '1 Month' },
  ];

  const getModelDisplayName = (value: string) => {
    const option = riskModelOptions.find(opt => opt.value === value);
    return option?.label || value;
  };

  const getCovarianceDisplayName = (value: string) => {
    const option = covarianceMatrixOptions.find(opt => opt.value === value);
    return option?.label || value;
  };

  const getTimePeriodDisplayName = (value: string) => {
    const option = timePeriodOptions.find(opt => opt.value === value);
    return option?.label || value;
  };

  const handleViewParameters = () => {
    setShowCovarianceDialog(true);
  };

  const handleResetToDefault = () => {
    setRiskModel('parametric');
    setCovarianceMatrix('GCV1415');
    setVarConfidence('95');
    setTimePeriodNumber('1');
    setTimePeriodUnit('day');
    setConfidenceError('');
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        riskModel,
        covarianceMatrix,
        varConfidence,
        timePeriodNumber,
        timePeriodUnit
      });
    }
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
      onClick={() => onOpenChange(false)}
    >
      <div
        data-testid='advanced-settings-dialog'
        style={{
          backgroundColor: theme.cardBackground,
          borderRadius: '8px',
          width: '700px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          margin: '16px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px',
          borderBottom: `1px solid ${theme.border}`
        }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              margin: 0,
              color: theme.textPrimary
            }}>
              Risk Model Configuration
            </h2>
            <p style={{
              fontSize: '16px',
              color: theme.textSecondary,
              margin: '4px 0 0 0'
            }}>
              Configure risk calculation parameters and Value at Risk settings
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              color: theme.textSecondary
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {/* Risk Model */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '16px',
              fontWeight: 600,
              color: theme.textPrimary
            }}>
              Risk Model
            </label>
            <select
              value={riskModel}
              onChange={(e) => setRiskModel(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                backgroundColor: theme.inputBackground,
                fontSize: '16px',
                color: theme.textPrimary
              }}
            >
              {riskModelOptions.map(option => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  style={{ color: option.disabled ? theme.textSecondary : theme.textPrimary }}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <div style={{ fontSize: '16px', color: theme.textSecondary, marginTop: '4px' }}>
              Method used for calculating portfolio risk metrics
            </div>
          </div>

          {/* Covariance Matrix */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <label style={{
                fontSize: '16px',
                fontWeight: 600,
                color: theme.textPrimary
              }}>
                Covariance Matrix
              </label>
              <button
                onClick={handleViewParameters}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '16px',
                  color: '#2563eb',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                <Info style={{ width: '16px', height: '16px' }} />
                View Parameters
              </button>
            </div>
            <select
              value={covarianceMatrix}
              onChange={(e) => setCovarianceMatrix(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                backgroundColor: theme.inputBackground,
                fontSize: '16px',
                color: theme.textPrimary
              }}
            >
              {covarianceMatrixOptions.map(option => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  style={{ color: option.disabled ? theme.textSecondary : theme.textPrimary }}
                >
                  {option.label}
                </option>
              ))}
            </select>
            <div style={{ fontSize: '16px', color: theme.textSecondary, marginTop: '4px' }}>
              Clearwater Wilshire Global Risk Model covering Equities, Fixed Income, and Commodities
            </div>
          </div>

          {/* VaR Settings */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '16px',
                fontWeight: 600,
                color: theme.textPrimary
              }}>
                VaR Confidence Level (%)
              </label>
              <input
                type="text"
                value={varConfidence}
                placeholder="Enter number"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setConfidenceError('');
                    setVarConfidence(value);
                  } else if (isNaN(Number(value))) {
                    setConfidenceError('Value should be numeric only');
                  } else if (Number(value) < 0 || Number(value) > 100) {
                    setConfidenceError('Value should be between 0-100');
                  } else {
                    setConfidenceError('');
                    setVarConfidence(value);
                  }
                }}
                style={{
                  width: '100%',
                  maxWidth: '250px',
                  padding: '12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px',
                  backgroundColor: theme.inputBackground,
                  fontSize: '16px',
                  color: theme.textPrimary,
                  MozAppearance: 'textfield',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
              />
              {confidenceError && (
                <div style={{
                  fontSize: '14px',
                  color: '#dc2626',
                  marginTop: '4px'
                }}>
                  {confidenceError}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'end' }}>
              <div style={{ flex: 0 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.textPrimary
                }}>
                  VaR Time Period
                </label>
                <input
                  type="text"
                  value={timePeriodNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || !isNaN(Number(value))) {
                      setTimePeriodNumber(value);
                    }
                  }}
                  placeholder="Enter number"
                  style={{
                    width: '120px',
                    padding: '12px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.inputBackground,
                    fontSize: '16px',
                    color: theme.textPrimary
                  }}
                />
              </div>
              <div style={{ flex: 0 }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.textPrimary,
                  visibility: 'hidden'
                }}>
                  Unit
                </label>
                <select
                  value={timePeriodUnit}
                  onChange={(e) => setTimePeriodUnit(e.target.value)}
                  style={{
                    width: '150px',
                    padding: '12px',
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    backgroundColor: theme.inputBackground,
                    fontSize: '16px',
                    color: theme.textPrimary
                  }}
                >
                  {timePeriodUnitOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Current Configuration Summary */}
          <div style={{
            backgroundColor: theme.inputBackground,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ fontWeight: 600, color: theme.textPrimary, marginBottom: '12px' }}>
              Current Configuration
            </div>
            <div style={{ fontSize: '16px', lineHeight: '1.5' }}>
              <div style={{ display: 'flex', marginBottom: '6px' }}>
                <span style={{ color: theme.textSecondary, width: '160px' }}>Model:</span>
                <span style={{ color: theme.textPrimary, fontWeight: 500 }}>
                  {getModelDisplayName(riskModel)}
                </span>
              </div>
              <div style={{ display: 'flex', marginBottom: '6px' }}>
                <span style={{ color: theme.textSecondary, width: '160px' }}>Covariance Matrix:</span>
                <span style={{ color: theme.textPrimary, fontWeight: 500 }}>
                  {getCovarianceDisplayName(covarianceMatrix)}
                </span>
              </div>
              <div style={{ display: 'flex', marginBottom: '6px' }}>
                <span style={{ color: theme.textSecondary, width: '160px' }}>VaR Confidence:</span>
                <span style={{ color: theme.textPrimary, fontWeight: 500 }}>{varConfidence ? `${varConfidence}%` : ''}</span>
              </div>
              <div style={{ display: 'flex' }}>
                <span style={{ color: theme.textSecondary, width: '160px' }}>Time Horizon:</span>
                <span style={{ color: theme.textPrimary, fontWeight: 500 }}>
                  {timePeriodNumber && timePeriodUnit ? `${timePeriodNumber} ${timePeriodUnit}${timePeriodNumber === '1' ? '' : 's'}` : ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          borderTop: `1px solid ${theme.border}`,
          padding: '16px 24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <Button
              variant="outlined"
              onClick={() => onOpenChange(false)}
              data-cy="advanced-settings-cancel-button"
            >
              Cancel
            </Button>
            <div style={{
              display: 'flex',
              gap: '8px'
            }}>
              <Button
                variant="outlined"
                onClick={handleResetToDefault}
              >
                Reset to Default
              </Button>
              <Button
                variant="contained"
                style={{
                  backgroundColor: theme.runButtonBackground,
                  color: theme.runButtonColor
                }}
                onClick={handleSave}
              >
                Save Configuration
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Covariance Parameters Dialog */}
      <CovarianceParametersDialog
        open={showCovarianceDialog}
        onOpenChange={setShowCovarianceDialog}
        matrixName={covarianceMatrix}
      />
    </div>
  );
}
